/**
 * Canonical PRINT_FILE_ISSUE reason sets (BLU-651 PR 1).
 *
 * Single source of truth shared by:
 *   - backend `print-agent-admin.ts` (re-check reason gate, wrong-data convert
 *     + manual-resolve guards),
 *   - backend `print-file-issue-feed.ts` (print-time auto-resolve allowlist),
 *   - backend `production-settings.ts` (org-configurable reason validation),
 *   - frontend `ResolveDialog.tsx` / `PrintFileIssueReasonsSection.tsx`.
 *
 * Two disjoint classes, split by what "the file exists on the cloud" MEANS:
 *
 *   - AUTO-RESOLVABLE reasons — the file was absent/unresolvable, so its
 *     appearance (or a successful print) genuinely fixes the issue. These are
 *     the ONLY reasons the print-time auto-resolve and cloud re-check may
 *     close. Positive allowlist by design: any reason outside this set —
 *     including org-configured custom reasons — must never auto-resolve,
 *     because existence ≠ correctness for them.
 *
 *   - WRONG-DATA reasons — the artwork or its product association is wrong
 *     (wrong design, position, colour, resolution, metadata…). Resolved only
 *     by correcting the source data or, where safe, uploading a corrected
 *     file; probing/printing a file alone proves nothing.
 */
/**
 * Phase 92 (D-10): a client's line should print on the 3PL's shared blank,
 * but its link to that blank fails a validity check. The file is not the
 * problem, the blank link is: the line must never print on another garment,
 * never auto-resolves, is never handed to the bridge (its admission only takes
 * `file_missing`) and refuses a replacement upload. Fixed by choosing the
 * blank again in Product Sync (client) or offering it again (3PL), then a
 * manual resolve.
 */
export const SUPPLY_MAPPING_INVALID_PRINT_FILE_REASON = 'supply_mapping_invalid';
/** Machine-generated diagnoses. Operators cannot select these directly. */
export const MACHINE_GENERATED_PRINT_FILE_REASONS = [
    'metadata_mismatch',
    SUPPLY_MAPPING_INVALID_PRINT_FILE_REASON,
];
/** Operator-reportable reasons where artwork exists but is WRONG. */
export const OPERATOR_WRONG_DATA_PRINT_FILE_REASONS = [
    'wrong_design',
    'color_mismatch',
    'low_resolution',
    'wrong_position',
    'other',
];
/** Every wrong-data reason, including machine-generated diagnoses. */
export const WRONG_DATA_PRINT_FILE_REASONS = [
    ...MACHINE_GENERATED_PRINT_FILE_REASONS,
    ...OPERATOR_WRONG_DATA_PRINT_FILE_REASONS,
];
/**
 * Reasons where the file was absent/unresolvable — the only ones a
 * file-appeared signal (cloud probe, successful print) may auto-resolve.
 * `file_missing` / `print_data_needed` are the raise-path discriminators;
 * `missing_artwork` is the print-station operator-facing value that routes
 * into the same raise path.
 */
export const AUTO_RESOLVABLE_PRINT_FILE_REASONS = [
    'file_missing',
    'missing_artwork',
    'print_data_needed',
];
/**
 * BLU-817 metadata marker: this `missing_artwork` issue was raised by the automatic
 * born-without-artwork detector, not by an operator at a station.
 *
 * NOT a new reason, deliberately. `missing_artwork` already means exactly this
 * condition — "the order line carries NO design reference at all" — and it owns the
 * entire remediation surface, every piece of which is hard-gated on that literal
 * string: the ResolveDialog's paste-a-Printway-URL section, `convertCustomIssueToUploadable`
 * (which mints a design ref via `custom-design-ref.ts` and turns the row into an
 * uploadable `file_missing`), and the operator guide on the Print Files list. A
 * distinct reason would render as a bare "Mark Resolved" with nothing to act on —
 * an unfixable row — and `missing_artwork` is already in
 * {@link AUTO_RESOLVABLE_PRINT_FILE_REASONS}, so the recovered design auto-resolves.
 *
 * The marker exists so the machine-detected class can still be told apart for the
 * things that genuinely differ: the operator-facing label and guidance, the film
 * station's "retrying will not help" copy, and hiding the unprintable garment from
 * the Film Print run.
 */
export const NO_ARTWORK_DETECTED_METADATA_KEY = 'no_artwork_detected';
/**
 * Phase 86 (D-22) metadata marker on a `file_missing` PRINT_FILE_ISSUE: this
 * size falls under a platen size rule (or a per-product override) and has no
 * matching platen variant file, so NO heal source may fill it (not the backup
 * cloud, the Drive designs folder, the automated designs promotion, nor the
 * standards resize). Fixing it is manual: upload the platen variant file in
 * product sync, after which the exact heal lands it and the existing
 * `file_missing` auto-resolve closes the issue.
 *
 * NOT a new reason, deliberately, for the same reason as
 * {@link NO_ARTWORK_DETECTED_METADATA_KEY}: `file_missing` already owns the
 * remediation and auto-resolve surface. The marker only picks the operator
 * guide.
 *
 * Value shape: `{ platen, platen_variant, side, unsupported_backend?: true }`.
 * `unsupported_backend` is set on a floor whose print storage cannot serve a
 * platen variant file at all (Azure), where the guide changes to "this floor
 * cannot print platen variant files".
 */
export const PLATEN_VARIANT_FILE_NEEDED_METADATA_KEY = 'platen_variant_file_needed';
/** Every reason an org may configure for the print-station issue dialog. */
export const VALID_PRINT_FILE_ISSUE_REASONS = [
    ...OPERATOR_WRONG_DATA_PRINT_FILE_REASONS,
    ...AUTO_RESOLVABLE_PRINT_FILE_REASONS,
];
// Set views for O(1) membership checks on untyped metadata strings.
export const WRONG_DATA_PRINT_FILE_REASON_SET = new Set(WRONG_DATA_PRINT_FILE_REASONS);
export const AUTO_RESOLVABLE_PRINT_FILE_REASON_SET = new Set(AUTO_RESOLVABLE_PRINT_FILE_REASONS);
export const VALID_PRINT_FILE_ISSUE_REASON_SET = new Set(VALID_PRINT_FILE_ISSUE_REASONS);
/**
 * Whether a wrong-data issue may overwrite its current lookup target.
 * Metadata mismatches must be fixed at the metadata layer: uploading to the
 * mismatched filename would preserve the bad association. A broken blank link
 * (`supply_mapping_invalid`) is fixed at the link, never with a file.
 */
export function canUploadReplacementForPrintFileReason(reason) {
    return WRONG_DATA_PRINT_FILE_REASON_SET.has(reason)
        && reason !== 'metadata_mismatch'
        && reason !== SUPPLY_MAPPING_INVALID_PRINT_FILE_REASON;
}
/** OPEN issue classification, including work held for a compatible daemon. */
export const PRINT_FILE_OPEN_CATEGORIES = ['auto_fetching', 'awaiting_bridge', 'needs_manual'];
export const BRIDGE_FETCH_STATES = ['awaiting_bridge', 'queued', 'served', 'downloaded', 'ripped', 'uploaded', 'failed'];
/** Stable wire codes; operator clients translate these instead of English prose.
 * `sibling_sides_pending`: Shopify confirmed the save, but the sides choice was
 * not copied to the product's other variant records yet (a retry copies it). */
export const PRINT_FILE_DETAIL_CODES = ['other_targets_pending', 'cloud_file_unchanged', 'cloud_file_missing', 'metadata_sync_pending', 'sibling_sides_pending'];
